import React, { useState } from 'react';
import { 
  Button, 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions, 
  TextField,
  Typography,
  CircularProgress,
  Box,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText
} from '@mui/material';
import { waitlistApi } from '../../services/api';
import { WaitlistRequest } from '../../types';
import { useAuth } from '../../auth/AuthContext';

interface WaitlistButtonProps {
  resourceId: number;
  resourceName: string;
  startDate: Date;
  endDate: Date;
  onSuccess?: () => void;
}

const WaitlistButton: React.FC<WaitlistButtonProps> = ({ 
  resourceId, 
  resourceName,
  startDate, 
  endDate,
  onSuccess 
}) => {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const handleOpen = () => {
    setOpen(true);
    setError(null);
    setSuccess(false);
  };

  const handleClose = () => {
    if (!loading) {
      setOpen(false);
      if (success && onSuccess) {
        onSuccess();
      }
    }
  };

  const handleSubmit = async () => {
    if (!user) {
      setError('You must be logged in to join the waitlist');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const waitlistRequest: WaitlistRequest = {
        resourceId,
        userId: user.id,
        startDate: startDate.toISOString(),
        endDate: endDate.toISOString(),
        notes,
        priority: 0 // Default priority
      };

      await waitlistApi.addToWaitlist(waitlistRequest);
      setSuccess(true);
    } catch (err: any) {
      console.error('Error adding to waitlist:', err);
      setError(err.response?.data?.error || 'Failed to add to waitlist. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Button 
        variant="outlined" 
        color="primary" 
        onClick={handleOpen}
        startIcon={<span role="img" aria-label="waitlist">📋</span>}
      >
        Join Waitlist
      </Button>

      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>Join Waitlist for {resourceName}</DialogTitle>
        <DialogContent>
          {success ? (
            <Alert severity="success" sx={{ mt: 2 }}>
              You have been added to the waitlist successfully! We'll notify you if this resource becomes available.
            </Alert>
          ) : (
            <>
              <Typography variant="body1" sx={{ mb: 2 }}>
                This resource is not available during the selected time period. Join the waitlist to be notified if it becomes available.
              </Typography>
              
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary">Time Period</Typography>
                <Typography variant="body1">
                  {startDate.toLocaleString()} - {endDate.toLocaleString()}
                </Typography>
              </Box>

              <TextField
                fullWidth
                label="Notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                multiline
                rows={3}
                placeholder="Optional notes about your waitlist request"
                sx={{ mb: 2 }}
              />

              {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {error}
                </Alert>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} disabled={loading}>
            {success ? 'Close' : 'Cancel'}
          </Button>
          {!success && (
            <Button 
              onClick={handleSubmit} 
              color="primary" 
              disabled={loading}
              variant="contained"
            >
              {loading ? <CircularProgress size={24} /> : 'Join Waitlist'}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </>
  );
};

export default WaitlistButton;
